import { useQuery } from '@connectrpc/connect-query';
import { createFileRoute } from '@tanstack/react-router';
import { LoaderDot } from '@thetreep/travel-ui';
import { z } from 'zod';

import { SearchResultsPage } from '@/features/searchResults/SearchResultsPage';
import { GuaranteeType } from '@/gen/mission/v1/guarantee_pb';
import { getMission } from '@/gen/mission/v1/mission-MissionService_connectquery';
import { TransportType } from '@/gen/mobility/v1/common_pb';
import {
	RentalVehicleListSort,
	TrainListSort,
} from '@/gen/mobility_search/v1/search_detail_pb';

const ALL_TRANSPORT_TYPES = [
	TransportType.TRAIN,
	TransportType.RENTAL_VEHICLE,
	TransportType.TAXI,
];

const searchFiltersSchema = z.object({
	trainSort: z.nativeEnum(TrainListSort).catch(TrainListSort.UNSPECIFIED),
	trainConnections: z.boolean().catch(true),
	rentalSort: z
		.nativeEnum(RentalVehicleListSort)
		.catch(RentalVehicleListSort.UNSPECIFIED),
	transports: z.array(z.nativeEnum(TransportType)).catch(ALL_TRANSPORT_TYPES),
});

export const Route = createFileRoute('/$missionId/search-results/$searchId/')({
	validateSearch: searchFiltersSchema,
	component: SearchResultsIndexRoute,
});

function SearchResultsIndexRoute() {
	const { searchId, missionId } = Route.useParams();
	const { transports } = Route.useSearch();
	const { data: missionData, isLoading } = useQuery(getMission, { missionId });

	// Wait for the guarantee type: the active transports drive the initial tab and
	// whether the comparison podium is shown, and are only read once on mount.
	if (isLoading) {
		return <LoaderDot className="mt-24 w-full justify-center" />;
	}

	// A replacement-vehicle search is rental-only whatever the URL says: reaching the
	// results without its filters (e.g. from the Stepper on the search page) would
	// otherwise fall back to every transport type and show a multimodal podium.
	const activeTransportTypes =
		missionData?.mission?.guaranteeType === GuaranteeType.REPLACEMENT_VEHICLE
			? [TransportType.RENTAL_VEHICLE]
			: transports;

	return (
		<SearchResultsPage
			searchId={searchId}
			missionId={missionId}
			activeTransportTypes={activeTransportTypes}
		/>
	);
}
